/**
 * Chapter loader for READER, OFFLINE-FIRST.
 *
 * Resolution order:
 *   1. Base PowerSync local (SQLite/OPFS) — `bible_verse_content`. Funciona sin
 *      network or server; bootstrap offline MVP source.
 *   2. Comando nativo Tauri (`get_chapter_native`) si corremos dentro de Tauri.
 *   3. Rust server HTTP API (`/api/v1/bible/verses/chapter`) as last
 *      resort (e.g. unseeded chapters).
 */

import { and, asc, bible_verse_content, eq, type PowerSyncDrizzleDB } from "@bso/powersync";
import { api } from "@/lib/api";
import { seedReaderContent } from "../seed/seed-reader";
import type { components } from "@bso/client";

type ChapterVerse = components["schemas"]["VerseResponse"];

/** Reads chapter from local DB. Returns [] if not seeded. */
export async function loadChapterFromLocal(
  db: PowerSyncDrizzleDB | null,
  version: string,
  book: number,
  chapter: number,
): Promise<ChapterVerse[]> {
  if (!db || Number.isNaN(book) || Number.isNaN(chapter)) {
    return [];
  }
  const rows = await db
    .select({
      version: bible_verse_content.version,
      book: bible_verse_content.book,
      chapter: bible_verse_content.chapter,
      verse: bible_verse_content.verse,
      text: bible_verse_content.text,
    })
    .from(bible_verse_content)
    .where(
      and(
        eq(bible_verse_content.version, version),
        eq(bible_verse_content.book, book),
        eq(bible_verse_content.chapter, chapter),
      ),
    )
    .orderBy(asc(bible_verse_content.verse));

  return rows.flatMap((r) =>
    r.verse === null || r.text === null
      ? []
      : [
          {
            version: r.version ?? version,
            book: r.book ?? book,
            chapter: r.chapter ?? chapter,
            verse: r.verse,
            text: r.text,
          },
        ],
  );
}

/**
 * Loads chapter from server (HTTP) — fallback for chapters outside
 * local seed. Does NOT throw if server unavailable: returning []
 * keeps reader usable offline (root error boundary should not trigger).
 */
async function loadChapterFromServer(
  version: string,
  book: number,
  chapter: number,
): Promise<ChapterVerse[]> {
  try {
    const { data, response } = await api.GET("/api/v1/bible/verses/chapter", {
      params: { query: { version, book, chapter } },
    });
    if (!response.ok) {
      return [];
    }
    return data ?? [];
  } catch (e) {
    console.warn("[reader] Could not fetch chapter from server (offline?)", e);
    return [];
  }
}

/**
 * Resolves chapter offline-first: local -> native Tauri -> HTTP API.
 */
export async function loadChapter(
  db: PowerSyncDrizzleDB | null,
  version: string,
  book: number,
  chapter: number,
): Promise<ChapterVerse[]> {
  const local = await loadChapterFromLocal(db, version, book, chapter);
  if (local.length > 0) {
    return local;
  }

  // Cold start: the local miss can be a race, not an absent chapter. The
  // startup seed (KJV + Strong) is idempotent and memoised; wait for it before
  // the network fallback so a seeded chapter never fires a doomed HTTP request
  // (the server 404s for a version it does not host, and the browser logs that
  // as a console error even though the reader handles it). Without a db there
  // is nothing to seed: the caller re-runs once the Drizzle client is ready.
  if (db) {
    await seedReaderContent(db);
    const seeded = await loadChapterFromLocal(db, version, book, chapter);
    if (seeded.length > 0) {
      return seeded;
    }
  }

  // Native Tauri command (offline on desktop/mobile outside seed)
  if (typeof globalThis !== "undefined" && "__TAURI_INTERNALS__" in globalThis) {
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const nativeVerses = await invoke<ChapterVerse[]>("get_chapter_native", {
        version,
        book,
        chapter,
      });
      if (nativeVerses.length > 0) {
        return nativeVerses;
      }
    } catch (e) {
      console.warn("Failed to get chapter natively, falling back to HTTP api", e);
    }
  }

  return loadChapterFromServer(version, book, chapter);
}
